import { FormEvent, useEffect, useMemo, useState } from 'react';
import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SvgIcon from '../../components/SvgIcon/SvgIcon';
import ModuleService from '../../services/module/ModuleService';
import MenuService from '../../services/menu/MenuService';
import { IModule } from '../../types/module';
import {
  ConfigureMenuRequest,
  CreateMenuRequest,
  MenuNode,
  MenuType,
} from '../../types/menu/MenuConfiguration';
import useApiErrorHandler from '../../hooks/useApiErrorHandler';
import useAlerts from '../../hooks/useAlerts';
import useConfirm from '../../hooks/useConfirmModal';

type FlatNode = MenuNode & { depth: number; ancestorIds: number[] };

const unwrap = <T,>(response: any, fallback: T): T =>
  (response?.data ?? fallback) as T;

const flattenTree = (
  nodes: MenuNode[],
  depth = 1,
  ancestors: number[] = []
): FlatNode[] =>
  nodes.flatMap((node) => [
    { ...node, depth, ancestorIds: ancestors },
    ...flattenTree(node.children ?? [], depth + 1, [...ancestors, node.id]),
  ]);

const subtreeHeight = (node: MenuNode): number =>
  1 + Math.max(0, ...(node.children ?? []).map(subtreeHeight));

const initialForm = {
  code: '',
  name: '',
  menuType: 'ITEM' as MenuType,
  parentId: '',
  orderIndex: 1,
  route: '',
  icon: '',
  expandedByDefault: false,
};

const MenuAdministrationPage = () => {
  const [modules, setModules] = useState<IModule[]>([]);
  const [moduleId, setModuleId] = useState('');
  const [tree, setTree] = useState<MenuNode[]>([]);
  const [searchResults, setSearchResults] = useState<MenuNode[] | null>(null);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<MenuNode | null>(null);
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const { handle } = useApiErrorHandler();
  const { show } = useAlerts();
  const confirm = useConfirm();

  const flatTree = useMemo(() => flattenTree(tree), [tree]);
  const selectedFlat = editing
    ? flatTree.find((node) => node.id === editing.id)
    : undefined;
  const parentOptions = flatTree.filter(
    (node) =>
      node.menuType === 'GROUP' &&
      node.depth + (editing ? subtreeHeight(editing) : 1) <= 3 &&
      node.id !== editing?.id &&
      !node.ancestorIds.includes(editing?.id ?? -1)
  );

  const loadTree = async (selectedModuleId = moduleId) => {
    if (!selectedModuleId) return;
    try {
      setBusy(true);
      const response = await MenuService.getMenuTree(Number(selectedModuleId));
      const nodes = unwrap<MenuNode[]>(response, []);
      setTree(nodes);
      setExpanded(
        new Set(
          flattenTree(nodes)
            .filter((node) => node.expandedByDefault)
            .map((node) => node.id)
        )
      );
      setSearchResults(null);
    } catch (error) {
      handle(error, 'No se pudo cargar el árbol de menús.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    ModuleService.getModules()
      .then((response) => {
        const list = unwrap<IModule[]>(response, []);
        setModules(list);
        if (list[0]) setModuleId(String(list[0].id));
      })
      .catch((error) => handle(error, 'No se pudieron cargar los módulos.'));
  }, []);

  useEffect(() => {
    if (moduleId) {
      setEditing(null);
      setForm(initialForm);
      setQuery('');
      loadTree(moduleId);
    }
  }, [moduleId]);

  useEffect(() => {
    if (!moduleId) return;
    const timer = window.setTimeout(async () => {
      if (!query.trim()) {
        setSearchResults(null);
        return;
      }
      try {
        const response = await MenuService.searchMenus(
          Number(moduleId),
          query.trim()
        );
        setSearchResults(unwrap<MenuNode[]>(response, []));
      } catch (error) {
        handle(error, 'No se pudo completar la búsqueda.');
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, moduleId]);

  const editNode = (node: MenuNode) => {
    const flatNode = flatTree.find((item) => item.id === node.id);
    const inferredParentId = flatNode?.ancestorIds.at(-1);
    setEditing(node);
    setForm({
      code: node.code,
      name: node.name,
      menuType: node.menuType,
      parentId: String(node.parentId ?? inferredParentId ?? ''),
      orderIndex: node.orderIndex ?? 1,
      route: node.route ?? '',
      icon: node.icon ?? '',
      expandedByDefault: node.expandedByDefault === true,
    });
  };

  const resetForm = () => {
    setEditing(null);
    setForm(initialForm);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!moduleId || !form.name.trim() || (!editing && !form.code.trim()))
      return;
    const parent = flatTree.find((node) => node.id === Number(form.parentId));
    if (parent && parent.depth >= 3) {
      show('error', 'Nivel no permitido', 'El menú solo admite tres niveles.');
      return;
    }

    try {
      setBusy(true);
      if (editing) {
        const payload: ConfigureMenuRequest = {
          menuId: editing.id,
          moveToRoot: form.parentId === '',
          ...(form.parentId !== '' && { parentId: Number(form.parentId) }),
          menuType: form.menuType,
          orderIndex: Number(form.orderIndex),
          name: form.name.trim(),
          route: form.route.trim() || undefined,
          clearRoute: !form.route.trim(),
          icon: form.icon.trim() || undefined,
          clearIcon: !form.icon.trim(),
          expandedByDefault: form.expandedByDefault,
        };
        await MenuService.configureMenu(payload);
      } else {
        const payload: CreateMenuRequest = {
          moduleId: Number(moduleId),
          code: form.code.trim(),
          name: form.name.trim(),
          menuType: form.menuType,
          parentId: form.parentId === '' ? null : Number(form.parentId),
          orderIndex: Number(form.orderIndex),
          route: form.route.trim() || null,
          icon: form.icon.trim() || null,
          expandedByDefault: form.expandedByDefault,
        };
        await MenuService.createMenu(payload);
      }
      show(
        'success',
        'Menú guardado',
        'La configuración se actualizó correctamente.'
      );
      resetForm();
      await loadTree();
    } catch (error) {
      handle(error, 'No se pudo guardar el menú.');
    } finally {
      setBusy(false);
    }
  };

  const remove = (node: MenuNode) =>
    confirm({
      title: 'Eliminar menú',
      message: `¿Deseas eliminar “${node.name}”?`,
      icon: 'warning',
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          setBusy(true);
          await MenuService.deleteMenu(node.id);
          if (editing?.id === node.id) resetForm();
          await loadTree();
        } catch (error) {
          handle(
            error,
            'No se pudo eliminar. Verifica que el menú no tenga hijos.'
          );
        } finally {
          setBusy(false);
        }
      },
    });

  const renderNode = (node: MenuNode, depth = 1): React.ReactNode => {
    const children = node.children ?? [];
    const isGroup = node.menuType === 'GROUP' || children.length > 0;
    const isOpen = expanded.has(node.id);
    return (
      <div key={node.id}>
        <div
          className="mb-2 flex items-center gap-2 rounded-lg border border-stroke bg-white p-3 dark:border-strokedark dark:bg-boxdark"
          style={{ marginLeft: `${(depth - 1) * 24}px` }}
        >
          {isGroup ? (
            <button
              type="button"
              aria-label={isOpen ? 'Contraer' : 'Expandir'}
              onClick={() =>
                setExpanded((current) => {
                  const next = new Set(current);
                  next.has(node.id) ? next.delete(node.id) : next.add(node.id);
                  return next;
                })
              }
              className="rounded p-1 hover:bg-gray-2 dark:hover:bg-meta-4"
            >
              <SvgIcon
                name="chevron-down-mini"
                className={`h-4 w-4 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
          ) : (
            <span className="w-6" />
          )}
          {node.icon && (
            <SvgIcon name={node.icon as any} className="h-5 w-5 text-primary" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-black dark:text-white">
                {node.name}
              </span>
              <span className="rounded bg-gray-2 px-2 py-0.5 text-xs dark:bg-meta-4">
                {node.menuType}
              </span>
            </div>
            <p className="truncate text-xs text-body">
              {node.code}
              {node.route ? ` · ${node.route}` : ''}
            </p>
          </div>
          <span className="text-xs">Orden {node.orderIndex}</span>
          <button
            type="button"
            onClick={() => editNode(node)}
            className="text-sm text-primary hover:underline"
          >
            Editar
          </button>
          <button
            type="button"
            onClick={() => remove(node)}
            className="text-sm text-danger hover:underline"
          >
            Eliminar
          </button>
        </div>
        {isOpen &&
          depth < 3 &&
          children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  const inputClass =
    'w-full rounded border border-stroke bg-transparent px-3 py-2 outline-none focus:border-primary dark:border-strokedark';

  return (
    <DefaultLayout>
      <BreadcrumbTwo />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <section className="rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="mb-5 grid gap-3 md:grid-cols-2">
            <label>
              <span className="mb-1 block text-sm font-medium">Módulo</span>
              <select
                className={inputClass}
                value={moduleId}
                onChange={(e) => setModuleId(e.target.value)}
              >
                {modules.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-sm font-medium">
                Buscar menú
              </span>
              <input
                className={inputClass}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nombre, código o ruta"
              />
            </label>
          </div>
          {busy && <p className="mb-3 text-sm text-primary">Actualizando…</p>}
          {searchResults
            ? searchResults.map((node) => renderNode({ ...node, children: [] }))
            : tree.map((node) => renderNode(node))}
          {!busy && (searchResults ?? tree).length === 0 && (
            <p className="py-8 text-center text-body">
              No hay menús para mostrar.
            </p>
          )}
        </section>

        <form
          onSubmit={submit}
          className="h-fit rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black dark:text-white">
              {editing ? 'Editar menú' : 'Crear menú'}
            </h2>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="text-sm text-primary"
              >
                Nuevo
              </button>
            )}
          </div>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm">Código</span>
              <input
                disabled={!!editing}
                required={!editing}
                className={inputClass}
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm">Nombre</span>
              <input
                required
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm">Tipo</span>
              <select
                className={inputClass}
                value={form.menuType}
                onChange={(e) =>
                  setForm({ ...form, menuType: e.target.value as MenuType })
                }
              >
                <option value="GROUP">GROUP</option>
                <option value="ITEM">ITEM</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm">Padre</span>
              <select
                className={inputClass}
                value={form.parentId}
                onChange={(e) => setForm({ ...form, parentId: e.target.value })}
              >
                <option value="">Raíz</option>
                {parentOptions.map((node) => (
                  <option key={node.id} value={node.id}>
                    {'— '.repeat(node.depth - 1)}
                    {node.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm">Ruta</span>
              <input
                className={inputClass}
                value={form.route}
                onChange={(e) => setForm({ ...form, route: e.target.value })}
                placeholder="/ruta (opcional)"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm">Icono</span>
              <input
                className={inputClass}
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="settings"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm">Orden</span>
              <input
                type="number"
                min="0"
                required
                className={inputClass}
                value={form.orderIndex}
                onChange={(e) =>
                  setForm({ ...form, orderIndex: Number(e.target.value) })
                }
              />
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.expandedByDefault}
                onChange={(e) =>
                  setForm({ ...form, expandedByDefault: e.target.checked })
                }
              />
              <span className="text-sm">Expandido por defecto</span>
            </label>
          </div>
          {selectedFlat && (
            <p className="mt-3 text-xs text-body">
              Nivel actual: {selectedFlat.depth} de 3
            </p>
          )}
          <button
            disabled={busy || !moduleId}
            className="mt-5 w-full rounded bg-primary px-4 py-2 text-white disabled:opacity-50"
          >
            Guardar
          </button>
        </form>
      </div>
    </DefaultLayout>
  );
};

export default MenuAdministrationPage;
